import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import type {
  IVacancySkillMatch,
  IVacancySkillRef,
} from 'core/hooks/CareerCenter/transform/transformSkillMatch';
import { PATHS } from 'router/config';
import { careerColor } from 'services/careerTokens';
import { careerType } from 'services/careerType';

export interface IVacancySkillMatchView {
  /** `undefined` — ещё грузится; `null` — сервер не смог посчитать. */
  match: IVacancySkillMatch | null | undefined;
  loading?: boolean;
  /** Ссылка на навык карты; без неё пробел — просто текст. */
  skillHref?: (slug: string) => string;
  /** Сколько навыков называть поимённо; `undefined` — все (панель деталей). */
  maxNames?: number;
}

/**
 * Соответствие навыков вакансии (ТЗ 0073) — ОДИН вид для карточки и панели.
 *
 * Четыре состояния, и ни одно не притворяется другим:
 * * загрузка — нейтральная строка без числа: временное «5 из 5» на медленной
 *   сети было бы ложным обещанием;
 * * ошибка — «не удалось проверить», а не «0 совпадений»;
 * * нет ни одного оценённого навыка — просьба оценить, а не «не хватает всего»;
 * * результат — «Twoje umiejętności: 4 z 5», ниже недостающие и неоценённые.
 */
export const VacancySkillMatch = memo(
  ({ match, loading = false, skillHref, maxNames }: IVacancySkillMatchView) => {
    const { t } = useTranslation();

    if (loading || match === undefined) {
      return (
        <Block role="status">
          <Neutral>{t('careerCenter.vacancies.match.loading')}</Neutral>
        </Block>
      );
    }

    if (match === null) {
      return (
        <Block role="status">
          <Neutral>{t('careerCenter.vacancies.match.error')}</Neutral>
        </Block>
      );
    }

    if (match.status === 'no_skills') {
      return (
        <Block>
          <Neutral>
            <InlineLink to={`${PATHS.careerProfile}#cv-skills`}>
              {t('careerCenter.vacancies.match.noSkills')}
            </InlineLink>
          </Neutral>
          <Names
            label={t('careerCenter.vacancies.match.unrated')}
            skills={match.unrated}
            skillHref={skillHref}
            max={maxNames}
          />
        </Block>
      );
    }

    return (
      <Block>
        <Headline>
          {t('careerCenter.vacancies.match.title', {
            matched: match.matchedCount,
            total: match.totalCount,
          })}
        </Headline>
        <Names
          label={t('careerCenter.vacancies.match.missing')}
          skills={match.missing}
          skillHref={skillHref}
          max={maxNames}
        />
        <Names
          label={t('careerCenter.vacancies.match.unrated')}
          skills={match.unrated}
          skillHref={skillHref}
          max={maxNames}
        />
      </Block>
    );
  },
);

interface INames {
  label: string;
  skills: IVacancySkillRef[];
  skillHref?: (slug: string) => string;
  max?: number;
}

const Names = ({ label, skills, skillHref, max }: INames) => {
  const { t } = useTranslation();
  if (!skills.length) return null;
  const shown = max === undefined ? skills : skills.slice(0, max);
  const rest = skills.length - shown.length;
  return (
    <Line>
      {label}{' '}
      {shown.map((skill, position) => (
        <React.Fragment key={skill.id}>
          {position ? ', ' : ''}
          {skill.slug && skillHref ? (
            <InlineLink to={skillHref(skill.slug)}>{skill.name}</InlineLink>
          ) : (
            skill.name
          )}
        </React.Fragment>
      ))}
      {rest > 0 ? t('careerCenter.vacancies.missingMore', { n: rest }) : ''}
    </Line>
  );
};

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 12px;
`;

const Headline = styled.p`
  margin: 0;
  color: ${careerColor.text};
  ${careerType.bodyStrong()};
`;

const Neutral = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const Line = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const InlineLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${careerColor.text};
  }
`;
